import React, { useState, useEffect, ChangeEvent } from 'react';
import { Row, Col, Alert } from 'react-bootstrap'
import Card from '../../../../components/Card'
import { DetailItem } from '../../../../features/details';
import { getDescriptions, getSiteType } from '../../../../services/api/site'
import { updateVmc, addVmc } from '../../../../services/api/vmc';
import { getVmcTypes } from '../../../../services/api/vmcType';
import { VmcStatusDto, VmcType, ApiError } from '../../../../services/openapi';
import { useKeycloak } from '@react-keycloak/web'
import { PropertyInfo } from '../../../../services/openapi/models/PropertyInfo';
import { Link, useNavigate } from "react-router-dom";
import { NotImplementedWarningModal } from '../../../../components/NotImplementedWarningModal';
import { addLinkedItem, deleteAllLinkedItems } from '../../../../services/api/linkedItem';
import { setPendingSelectedId } from '../../../../hooks/pendingSelection';
import { useTranslation } from 'react-i18next';
import { popNavContext } from '../../../../hooks/navContext';

const VmcDetails = () => {
    const [vmc, setVmc] = useState<any>();
    const [showInputEle, setShowInputEle] = useState<boolean>(false);
    const [showModal, setShowModal] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string[]>([]);
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
    const [siteDescriptions, setSiteDescriptions] = useState<Record<string,string>>({});

    const { keycloak, initialized } = useKeycloak();
    const token = keycloak.token || '';

    const navigate = useNavigate();
    const { t } = useTranslation();

    const navigateBack = (selectedId = vmc?.id) => {
        const nav = popNavContext();
        let route = nav?.from;
        if (!route) { 
            route = 'lists/vmcs';
        }

        if (nav?.id) {
            if (route === 'details/transaction') {
                const transaction = JSON.parse(localStorage.getItem('Transaction') || 'null');
                if (transaction && transaction.id === nav.id) {
                    route = 'details/transaction';
                }
                else {
                    route = 'lists/transactions';
                }
            }
        }

        if (route === 'lists/vmcs') {
            setPendingSelectedId('vmcs', selectedId);
        }

        navigate('/dashboard/'+route);
    };

    const setVmcData = (data: VmcStatusDto) => {
        localStorage.setItem('Vmc', JSON.stringify(data));
    }

    const toggleInputElements = (show: boolean) => {
        setShowInputEle(show);
        if (show) {
            document.querySelector('.errorMsg')?.classList.add("d-none");
            errorMsg.length = 0;
        }
    }

    const pushErrorMessages = (errors: any) => {
        Object.entries<Record<string, any>>(errors).forEach((k: [string, Record<string, any>]) => {
            k[1].forEach((msg: string) => {
                errorMsg.push(msg);
            })
        });
    }

    const showErrorMessages = (errors: string[] | void) => {
        if (errors) {
            errors.forEach((msg: string) => { errorMsg.push(msg); })
        }

        document.querySelector('.errorMsg')?.classList.remove("d-none");
        setIsRefreshing(false);
    }

    const handleVmcChange = async (e: ChangeEvent<HTMLInputElement>, fieldKey: keyof typeof vmc) => {
        const newValue = e.target.value;

        //vmc[key] = (e.target.value); if (key === 'siteId') { vmc.vmcIds = null } setVmc(vmc);

        setVmc(prev => ({
            ...prev,
            [fieldKey]: newValue,
            vmcIds: fieldKey === 'siteId' ? null : prev.vmcIds,
        }));

        if (fieldKey === 'siteId' && newValue) {
            try {
            const siteType = await getSiteType(token, newValue);
            setVmc(prev => ({
                ...prev,
                siteType,
            }));
            } catch (error) {
                try {
                    document.querySelector('.errorMsg')?.classList.remove('d-none');
                    pushErrorMessages(JSON.parse((error as ApiError).body)['errors']);
                    showErrorMessages();
                } catch (ex) {
                    console.log(ex);
                    showErrorMessages(['Error handling response']);
                }
            }
        }
    };
    

    useEffect(() => {
        if (vmc == null) {
            getDescriptions(token).then(map => {
                setSiteDescriptions(map);
            }).catch((ex: ApiError) => {
                console.log(ex);
                showErrorMessages(["Error handling response"]);
            });

            const json = localStorage.getItem('Vmc') ?? "";
            const item = JSON.parse(json);

            // Show the status from vmcStatusEnum: Online (1), not ready (2, card reader disabled; the backend's
            // vmcStatus is then the resource key vmcStatus.notReady), otherwise Offline. A machine that isn't saved yet is offline.
            item["vmcStatus"] = item["id"] === 0 ? "Offline"
                : item["vmcStatusEnum"] === 1 ? "Online"
                : item["vmcStatusEnum"] === 2 ? t("vmcStatus.notReady")
                : "Offline";

            setVmc(item);

            if (item["id"] === 0) {
                toggleInputElements(true);
            }
        }
        else if (!vmc.siteType) {
            getSiteType(token, vmc.siteId).then((siteType) =>
                setVmc(prev => ({
                                ...prev,
                                siteType,
                            }))
            );            
        }
    }, [isRefreshing, vmc]);

    // When the site type of a new machine has only one VMC type, link it straight away; otherwise it is set afterwards via the Type button
    useEffect(() => {
        if (vmc?.id !== 0 || !vmc.siteType) {
            return;
        }

        getVmcTypes(token, "0-25", { siteType: vmc.siteType }).then((result) => {
            const types = result.body as VmcType[];
            const onlyType = types.length === 1 ? types[0] : null;
            setVmc(prev => ({
                ...prev,
                vmcTypeId: onlyType?.id ?? 0,
                type: onlyType?.description ?? "",
            }));
        }).catch((ex: ApiError) => {
            console.log(ex);
            showErrorMessages(["Error handling response"]);
        });
    }, [vmc?.id, vmc?.siteType]);

    function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
        e.preventDefault();

        document.querySelector('.errorMsg')?.classList.add("d-none");
        errorMsg.length = 0;        

        if (e.currentTarget.checkValidity()) {
            setIsRefreshing(true);
            let v: VmcStatusDto = vmc;
            setVmcData(v);
            if (v.id === 0) {
                addVmc(token, v).then((id) => {
                    if (id) {
                        let nv: VmcStatusDto = { ...vmc, id: id, vmcStatus: "Offline", vmcStatusEnum: 0 };
                        setVmc(nv);
                        setVmcData(nv);
                        setIsRefreshing(false);
                        // The type was linked automatically, so there is nothing left to do here. Otherwise stay on
                        // the page: the type can only be linked once the machine exists (Type button).
                        if (nv.vmcTypeId) {
                            navigateBack(id);
                        }
                    }
                }).catch((error: ApiError) => {
                    try {
                        document.querySelector('.errorMsg')?.classList.remove("d-none");
                        Object.entries<Record<string, any>>(JSON.parse(error.body)["errors"]).forEach((k) => { k[1].forEach((msg: string) => { errorMsg.push(msg); setErrorMsg(errorMsg); }) });
                        setIsRefreshing(false);
                    } catch (ex) {
                        showErrorMessages(["Error handling response"]);
                    }
                });
            }
            else {
                updateVmc(token, v.id ?? 0, v).then(() =>  navigateBack()).catch((error: ApiError) => {
                    try {
                        pushErrorMessages(JSON.parse(error.body)["errors"]);
                        showErrorMessages();
                    } catch (ex) {
                        showErrorMessages(["Error handling response"]);
                    }
                });
            }
            toggleInputElements(!showInputEle);
        }
    }

    return (
        <>
            <div className="">
                <NotImplementedWarningModal
                    showModal={showModal}
                    handleClose={() => {
                        setShowModal(false);
                    }}
                />
            </div>
            <Row className="">
                <Col className="">
                    <Card className="rounded">
                        <Card.Body className="">
                            <form onSubmit={e => handleSubmit(e)}>
                                <Row className="d-none d-md-block">
                                    <Col>&nbsp;</Col>
                                </Row>
                                <Row className="d-none d-md-block">
                                    <Col>&nbsp;</Col>
                                </Row>
                                <Row className="">
                                    <Col sm="12" className="">
                                        <h4 className="mb-2 qvend-title">{vmc?.id ? t("automaatdetails") : t("automaatToevoegen")}</h4>
                                    </Col>
                                </Row>
                                <Row className="">
                                    <Col sm="12" xs="12" className="mt-2">
                                        <div className="table-responsive-lg" style={{ overflowX: 'hidden' }}>
                                            <div>
                                                {vmc && Object.entries<any>(vmc)
                                                    .filter(([key, value]) => {
                                                        const propInfo = vmc["propertyNames"][key];
                                                        if (!propInfo) return false;
                                                        //if (!vmc[key]) return false;
                                                        if (propInfo.editType === '' && value === null || propInfo.editType === 'hidden') return false;
                                                        if (propInfo.hideOnNull && (value === null || value === undefined)) return false;
                                                        
                                                        // Check role access
                                                        if (!hasRole(vmc, key, keycloak)) return false;                                                    
                                                        
                                                        // Check conditionToShowFieldName with conditionToShowValue
                                                        if (propInfo.conditionToShowFieldName && propInfo.conditionToShowValue) {
                                                            const actualValue = vmc[propInfo.conditionToShowFieldName];
                                                            if (actualValue !== propInfo.conditionToShowValue) return false;
                                                        }
                                                        
                                                        return true;
                                                    })
                                                    .sort((a, b) => 
                                                        vmc["propertyNames"][a[0]].sortOrderLevel - 
                                                        vmc["propertyNames"][b[0]].sortOrderLevel
                                                    )
                                                    .map(([key, value], index) => {
                                                        const propInfo = vmc["propertyNames"][key];
                                                        
                                                        return (
                                                            <DetailItem 
                                                                key={propInfo.sortOrderLevel} 
                                                                propertyName={propInfo.name} 
                                                                defaultValue={value ?? ""} 
                                                                rowColor={index % 2 ? "#cfd5ea" : "#e9ebf5"}
                                                                editType={propInfo.editType} 
                                                                pattern={propInfo.valPattern} 
                                                                hideOnNull={propInfo.hideOnNull}
                                                                handleDoubleClick={() => toggleInputElements(true)}
                                                                handleChange={(e: ChangeEvent<HTMLInputElement>) => handleVmcChange(e, key)} 
                                                                showInputEle={showInputEle} 
                                                                possibilities={propInfo.predefinedValues} 
                                                                possibilityDescriptions={siteDescriptions} 
                                                                conditionToShowReferenceValue={vmc[propInfo.conditionToShowFieldName]} 
                                                                conditionToShowFieldName={propInfo.conditionToShowFieldName} 
                                                                conditionToShowValue={propInfo.conditionToShowValue}
                                                            />
                                                        );
                                                    })}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                                <Row className="d-none errorMsg">
                                    <Col>
                                        {
                                            errorMsg.map((item, index) => {
                                                return <Alert key={index} variant="warning" className="alert" role="alert">
                                                    <span>{item}</span>
                                                </Alert>;
                                            })
                                        }
                                    </Col>
                                </Row>
                                <Row style={{ paddingTop: "12px" }}>
                                    <Col>
                                        {vmc && (vmc?.id > 0) &&  (vmc as VmcStatusDto).type ?
                                            (<button type="button" className="btn btn-fixed-width" style={{ fontSize: '11px' }} onClick={() => {
                                                navigate('/dashboard/details/vmc/vmc-linked-products');
                                            }}>{t("vmc.button.Products")}</button>) : undefined}
                                    </Col>
                                    <Col className="text-end">
                                        {vmc && (<button type="button" className="btn btn-fixed-width" style={{ fontSize: '11px' }} disabled={!(vmc.id > 0)} onClick={() => {
                                            document.querySelector('.errorMsg')?.classList.add("d-none");
                                            errorMsg.length = 0;

                                            deleteAllLinkedItems(token, vmc.id ?? 0, 'Vmc', "VmcType").then(() => {
                                                if (vmc.type) {
                                                    addLinkedItem(token, { parentId: vmc.id ?? 0, parentType: 'Vmc', linkedEntityId: vmc.vmcTypeId, linkedEntityType: "VmcType" }).then(() => {
                                                        navigate('/dashboard/items-linkers/vmcType-to-vmc');
                                                    }).catch((error: ApiError) => {
                                                        try {
                                                            document.querySelector('.errorMsg')?.classList.remove("d-none");
                                                            Object.entries<Record<string, any>>(JSON.parse(error.body)["errors"]).forEach((k) => { k[1].forEach((msg: string) => { errorMsg.push(msg); setErrorMsg(errorMsg); }) });
                                                        } catch (ex) {
                                                            errorMsg.push("Error handling response");
                                                            setErrorMsg(errorMsg);
                                                        }
                                                    });
                                                }
                                                else {
                                                    navigate('/dashboard/items-linkers/vmcType-to-vmc');
                                                }
                                            }).catch((error: ApiError) => {
                                                try {
                                                    document.querySelector('.errorMsg')?.classList.remove("d-none");
                                                    Object.entries<Record<string, any>>(JSON.parse(error.body)["errors"]).forEach((k) => { k[1].forEach((msg: string) => { errorMsg.push(msg); setErrorMsg(errorMsg); }) });
                                                } catch (ex) {
                                                    errorMsg.push("Error handling response");
                                                    setErrorMsg(errorMsg);
                                                }
                                            });
                                        }}>{t("vmc.button.vmcType")}</button>)}
                                    </Col>
                                </Row>
                                <Row style={{ paddingTop: "3px" }}>
                                    <Col>
                                        {vmc && (vmc?.id > 0) && (<button type="button" className="btn btn-fixed-width" style={{ fontSize: '11px' }} onClick={() => {
                                            document.querySelector('.errorMsg')?.classList.add("d-none");
                                            errorMsg.length = 0;

                                            deleteAllLinkedItems(token, vmc.id ?? 0, 'Vmc', "Location").then(() => {
                                                if (vmc.locationId) {
                                                    addLinkedItem(token, { parentId: vmc.id ?? 0, parentType: 'Vmc', linkedEntityId: vmc.locationId, linkedEntityType: "Location" }).then(() => {
                                                        navigate('/dashboard/items-linkers/location-to-vmc');
                                                    });
                                                }
                                                else {
                                                    navigate('/dashboard/items-linkers/location-to-vmc');
                                                }
                                            }).catch((error: ApiError) => {
                                                try {
                                                    document.querySelector('.errorMsg')?.classList.remove("d-none");
                                                    Object.entries<Record<string, any>>(JSON.parse(error.body)["errors"]).forEach((k) => { k[1].forEach((msg: string) => { errorMsg.push(msg); setErrorMsg(errorMsg); }) });
                                                } catch (ex) {
                                                    errorMsg.push("Error handling response");
                                                    setErrorMsg(errorMsg);
                                                }
                                            });
                                        }}>{t("vmc.button.location")}</button>)}
                                    </Col>
                                    <Col className="text-end">                                        
                                    </Col>
                                </Row>
                                <Row style={{ paddingTop: "3px" }}>
                                    <Col>
                                        
                                            <button type="button" className="btn btn-fixed-width" style={{ fontSize: '11px', backgroundColor: "red" }} onClick={() => { navigateBack(); }}>{showInputEle ? t("button.cancel") : t("button.back")}</button>
                                        
                                    </Col>
                                    <Col className="text-end">
                                        {showInputEle ?
                                            (vmc && vmc?.id > 0 ? (<button type="submit" className="btn btn-fixed-width" style={{ fontSize: '11px' }}> {t("button.save")}</button>) :
                                            (<button type="submit" className="btn btn-fixed-width" style={{ fontSize: '11px' }}> {vmc && vmc.id > 0 ? t("button.save") : t("button.add")}</button>)):
                                            (<button type="button" className="btn btn-fixed-width" style={{ fontSize: '11px' }} onClick={(e) => { e.preventDefault(); toggleInputElements(true); }}> {t("button.edit")}</button>)
                                        }
                                    </Col>
                                </Row>
                            </form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </>
    )
}

export default VmcDetails;

function hasRole(terminal: any, key: string, keycloak: any) {
    if (!(terminal && terminal!.propertyNames)) {
        return false;
    }

    const roleNames = terminal["propertyNames"][key].roleNames;
    let result: boolean = false;
    var returnValue = roleNames === null || keycloak && roleNames.map((role: string) => { result = result || keycloak.hasResourceRole(role) }) && result;
    return returnValue;
}